import type { HandoverPhoto } from '@sivan/shared';
import { useEffect, useId, useRef } from 'react';

/** Full-screen view of a finding's photos, with previous / next (keyboard arrows too). */
export function PhotoViewer({
  photos,
  index,
  urlOf,
  onIndex,
  onClose,
}: {
  readonly photos: readonly HandoverPhoto[];
  readonly index: number | null;
  readonly urlOf: (photo: HandoverPhoto) => string;
  readonly onIndex: (index: number) => void;
  readonly onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const open = index !== null;
  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  const photo = index === null ? undefined : photos[index];
  const count = photos.length;
  // RTL: "next" is to the left.
  const go = (step: number) => {
    if (index !== null && count > 0) onIndex((index + step + count) % count);
  };
  return (
    <dialog
      ref={ref}
      className="photo-viewer"
      aria-labelledby={titleId}
      onClose={onClose}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') go(1);
        if (e.key === 'ArrowRight') go(-1);
      }}
    >
      <header className="photo-viewer__bar">
        <h2 id={titleId}>
          תמונה {index === null ? 0 : index + 1} מתוך {count}
        </h2>
        <button
          type="button"
          className="button button--ghost photo-viewer__close"
          onClick={onClose}
          aria-label="סגירת התצוגה"
        >
          ✕
        </button>
      </header>
      {photo !== undefined && (
        <img
          className="photo-viewer__image"
          src={urlOf(photo)}
          alt={`תמונה ${String((index ?? 0) + 1)} של הליקוי`}
          width={photo.width}
          height={photo.height}
        />
      )}
      {count > 1 && (
        <div className="photo-viewer__nav">
          <button
            type="button"
            className="button"
            onClick={() => {
              go(-1);
            }}
          >
            → הקודמת
          </button>
          <button
            type="button"
            className="button"
            onClick={() => {
              go(1);
            }}
          >
            הבאה ←
          </button>
        </div>
      )}
    </dialog>
  );
}

/** A row of square thumbnails; activating one opens the viewer at it. */
export function PhotoStrip({
  photos,
  urlOf,
  onOpen,
  label,
}: {
  readonly photos: readonly HandoverPhoto[];
  readonly urlOf: (photo: HandoverPhoto) => string;
  readonly onOpen: (index: number) => void;
  readonly label: string;
}) {
  if (photos.length === 0) return null;
  return (
    <ul className="photo-strip" aria-label={label}>
      {photos.map((photo, index) => (
        <li key={photo.id}>
          <button
            type="button"
            className="photo-strip__item"
            aria-label={`הצגת תמונה ${String(index + 1)} מתוך ${String(photos.length)}`}
            onClick={() => {
              onOpen(index);
            }}
          >
            <img
              src={urlOf(photo)}
              alt=""
              loading="lazy"
              width={photo.width}
              height={photo.height}
            />
          </button>
        </li>
      ))}
    </ul>
  );
}
